import { useCallback, useMemo, useState } from "react";
import { ColumnType } from "antd/es/table/interface";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  useLazyGetDataBlocksQuery,
  useGetDataTemplatesQuery,
} from "@/redux/service";
import BaseAvatarGroup from "@/components_v2/Avatar/BaseAvatarGroup";
import { SharingRights } from "@/shared/utils/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";
import EvidenceFilters from "./EvidenceFilters";
import UserAvatar from "@/domains/users/components/UserAvatar";
import { useDataBlockRelatedData } from "@/domains/datablocks/hooks/useDataBlockRelatedData";
import { useResponsive } from "@/shared/hooks/useBreakpoints";
import { CollapsedAvatarGroupLabel } from "@/components_v2/Avatar/types";
import SharingRightsTooltip from "@/domains/evidence/components/SharingRightsTooltip";
import { LanguageResponse } from "@/redux/common/types";
import { BaseOrganizationResponse } from "@/redux/organizations/types";
import {
  getEvidenceContributors,
  getEvidenceLanguages,
  getEvidenceTitle,
} from "@/domains/evidence/hooks/useEvidenceFields";
import {
  EvidenceFiltersState,
  EvidenceTableProps,
  NormalizedEvidence,
} from "@/domains/evidence/types";
import {
  BaseDataTemplateResponse,
  ContentType,
  DataBlockListResponse,
  DataTemplateResponse,
} from "@/redux/datablocks/types";
import { BaseUserResponse } from "@/redux/users/types";

import "../styles.css";
import { UserAvatarProps } from "@/domains/users/types";

const { Text } = Typography;

const EvidenceTable = <T extends DataBlockListResponse>({
  columnNames,
  filterNames = [],
  defaultFilters = {},
  rowSelection,
  additionalColumns,
  pagination,
}: EvidenceTableProps<T, NormalizedEvidence>) => {
  const { isMobile } = useResponsive();

  const { t } = useTranslation();

  const [filters, setFilters] = useState<EvidenceFiltersState>(
    defaultFilters as EvidenceFiltersState,
  );
  const [pageContributorIds, setPageContributorIds] = useState<number[]>([]);
  const [pageLanguageIds, setPageLanguageIds] = useState<number[]>([]);
  const { data: templates } = useGetDataTemplatesQuery({
    pagination: "off",
    isEvidence: true,
  }) as { data: DataTemplateResponse[] | undefined };
  const { data: users } = useDataBlockRelatedData(
    pageContributorIds,
    ContentType.Users,
  ) as { data: BaseUserResponse[] | null };
  const { data: languages } = useDataBlockRelatedData(
    pageLanguageIds,
    ContentType.Languages,
  ) as { data: LanguageResponse[] | null };

  const userIdMapping = useMemo(() => {
    if (!users) return;
    return users.reduce<Record<number, BaseUserResponse>>((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});
  }, [users]);

  const languageIdMapping = useMemo(() => {
    if (!languages) return;
    return languages.reduce<Record<number, LanguageResponse>>(
      (acc, language) => {
        acc[language.id] = language;
        return acc;
      },
      {},
    );
  }, [languages]);

  const templateFieldsMapping = useMemo(() => {
    if (!templates) return;
    return templates.reduce<Record<number, DataTemplateResponse>>(
      (acc, template) => {
        acc[template.id] = template;
        return acc;
      },
      {},
    );
  }, [templates]);

  const columns: Record<string, ColumnType<NormalizedEvidence>> = useMemo(
    () => ({
      title: {
        title: t("datablocks.labels.title"),
        fixed: !isMobile ? "left" : undefined,
        dataIndex: "title",
        sorter: true,
        width: 350,
        ellipsis: true,
        render: (_, value: NormalizedEvidence) => (
          <div className="data-blocks-table-title-container">
            <Link
              to={`/evidence/${value.id}`}
              state={
                rowSelection?.enabled ? undefined : { showBackButton: true }
              }
              target={rowSelection?.enabled ? "_blank" : undefined}
            >
              <Text
                style={{ maxWidth: 350 }}
                ellipsis={{
                  symbol: "...",
                  tooltip: true,
                }}
              >
                {value.title}
              </Text>
            </Link>
          </div>
        ),
      },
      type: {
        title: t("datablocks.labels.dataTemplateType"),
        dataIndex: "template",
        render: (value: BaseDataTemplateResponse) => {
          return value.title;
        },
      },
      organization: {
        title: t("datablocks.labels.signatory"),
        dataIndex: "organization",
        width: 200,
        render: (value: BaseOrganizationResponse) => (
          <Text
            style={{ maxWidth: 200 }}
            ellipsis={{
              symbol: "...",
              tooltip: true,
            }}
          >
            <Link to={`/signatories/${value.id}`}>{value.name}</Link>
          </Text>
        ),
      },
      contributors: {
        title: t("datablocks.labels.contributors"),
        dataIndex: "contributors",
        render: (value: number[]) => {
          if (!userIdMapping) return;
          const users = value
            .map((userId) => userIdMapping[userId])
            .filter((i) => !!i);
          return (
            <BaseAvatarGroup<UserAvatarProps & CollapsedAvatarGroupLabel>
              AvatarComponent={UserAvatar}
              avatars={users.map((user) => ({
                user,
                label: user.fullName,
                tooltip: user.fullName,
              }))}
            />
          );
        },
      },
      sharingRights: {
        title: (
          <div className="data-block-table-sharing-rights-header">
            <p>{t("datablocks.labels.sharingRights")}</p>
            <SharingRightsTooltip>
              <InfoCircleOutlined />
            </SharingRightsTooltip>
          </div>
        ),
        dataIndex: "sharingRights",
        render: (value: string) => {
          const sharingRights = SharingRights.getItemByValue(value);
          return (
            <BaseTag
              icon={sharingRights.icon}
              label={sharingRights.label}
              className={sharingRights.className}
            />
          );
        },
      },
      languages: {
        title: t("datablocks.labels.languages"),
        dataIndex: "languages",
        render: (value: number[]) => {
          if (!languageIdMapping) return;
          const languages = value
            .map((languageId) => languageIdMapping[languageId]?.name)
            .filter((i) => !!i);
          return languages.join(", ");
        },
      },
      createdAt: {
        title: t("datablocks.labels.createdAt"),
        dataIndex: "createdAt",
        key: "createdAt",
        sorter: true,
        defaultSortOrder: "descend",
        render: (createdAt) => DateTime.fromISO(createdAt).toRelative(),
      },
      updatedAt: {
        title: t("datablocks.labels.lastModified"),
        dataIndex: "updatedAt",
        key: "updatedAt",
        sorter: true,
        defaultSortOrder: "descend",
        render: (updatedAt) => DateTime.fromISO(updatedAt).toRelative(),
      },
      createdBy: {
        title: t("datablocks.labels.owner"),
        dataIndex: "createdBy",
        sorter: true,
        render: (value: BaseUserResponse) => (
          <div className="data-blocks-table-avatar-container">
            <UserAvatar user={value} />
            <p>{value.fullName}</p>
          </div>
        ),
      },
      ...additionalColumns,
    }),
    [userIdMapping, isMobile, additionalColumns, languages],
  );

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<NormalizedEvidence>,
      ),
    [columns, columnNames],
  );

  const normalizeData = useCallback(
    (dataBlocks: DataBlockListResponse[]) => {
      const uniqueContributorIds = new Set<number>();
      const uniqueLanguageIds = new Set<number>();
      const normalizedBlocks = dataBlocks.map(
        (block: DataBlockListResponse): NormalizedEvidence => {
          const languages = getEvidenceLanguages(block);
          languages.forEach((id) => uniqueLanguageIds.add(id));
          const contributors = getEvidenceContributors(block);
          contributors.forEach((id) => uniqueContributorIds.add(id));
          return {
            ...block,
            title: getEvidenceTitle(block),
            contributors,
            languages,
          };
        },
      );
      setPageContributorIds(Array.from(uniqueContributorIds));
      setPageLanguageIds(Array.from(uniqueLanguageIds));
      return normalizedBlocks;
    },
    [templateFieldsMapping],
  );

  const handleRowClick = (record: NormalizedEvidence) => {
    if (rowSelection?.enabled && rowSelection.onSelect) {
      const isCurrentlySelected = rowSelection.selectedRows?.some(
        (selectedBlock) => selectedBlock.id === record.id,
      );

      let newSelectedBlocks;
      if (isCurrentlySelected) {
        newSelectedBlocks =
          rowSelection.selectedRows?.filter(
            (block) => block.id !== record.id,
          ) || [];
      } else {
        if (rowSelection.multiple) {
          newSelectedBlocks = [...(rowSelection.selectedRows || []), record];
        } else {
          newSelectedBlocks = [record];
        }
      }

      rowSelection.onSelect(newSelectedBlocks);
    }
  };

  return (
    templateFieldsMapping && (
      <div className="data-blocks-table">
        <EvidenceFilters
          defaultFilters={defaultFilters}
          filterNames={filterNames}
          onFiltersChange={setFilters}
        />
        <QueryTable<T, NormalizedEvidence>
          query={useLazyGetDataBlocksQuery}
          filters={filters}
          columns={mappedColumns}
          normalizeData={normalizeData}
          rowSelection={rowSelection}
          pagination={pagination}
          onRow={(record) => ({
            onClick: (event) => {
              if (
                event.target instanceof HTMLElement &&
                (event.target.closest("button") ||
                  event.target.closest("a") ||
                  event.target.closest('input[type="checkbox"]'))
              ) {
                return;
              }

              handleRowClick(record);
            },
          })}
        />
      </div>
    )
  );
};

export default EvidenceTable;
